import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";

/**
 * Safely render user-generated HTML.
 *
 * - Falls back to plain text when input doesn't look like HTML.
 * - v288 — Aggressively hide broken / unresolvable / 1×1 tracker images
 *   AND their now-empty wrapper (`<p>` / `<div>` / `<a>`). Email signatures
 *   love to reference CID: attachments, hotlinked corporate assets behind
 *   auth, or 1-pixel tracking beacons — all of which used to leave large
 *   framed rectangles in the ticket body (see user report Feb 2026 : « quand
 *   une image ne s'affiche pas, il ne faut pas que cela fasse des espaces
 *   vide »). The real attachment is always available in the ticket
 *   attachments pane anyway.
 */
export default function RichView({ html, className = "" }) {
  const ref = useRef(null);
  const value = html || "";
  const looksHtml = /<[a-z][^>]*>/i.test(value);

  useEffect(() => {
    if (!looksHtml || !ref.current) return;
    const root = ref.current;

    // Hide the given <img> AND collapse its wrapper chain when the wrapper
    // has no other content left. Walking up avoids leaving <p> or <a> shells
    // that keep margin / border / fixed min-height and produce the visible
    // empty frames the user complained about.
    const hideImgAndEmptyParents = (img) => {
      img.style.display = "none";
      let p = img.parentElement;
      while (p && p !== root) {
        const hasRealContent = p.querySelector(
          'img:not([style*="display: none"]),video,iframe,svg,canvas,object'
        );
        if (hasRealContent) break;
        if (p.textContent && p.textContent.trim()) break;
        p.style.display = "none";
        p = p.parentElement;
      }
    };

    const isPixelTracker = (i) =>
      (i.naturalWidth <= 1 && i.naturalHeight <= 1) ||
      i.naturalWidth === 0 || i.naturalHeight === 0;

    const process = (img) => {
      const src = (img.getAttribute("src") || "").trim();
      // Sources that never resolve in a browser context.
      if (!src || /^cid:/i.test(src)) {
        hideImgAndEmptyParents(img);
        return;
      }
      // Already errored / already loaded broken (cached network failure).
      if (img.complete) {
        if (isPixelTracker(img)) hideImgAndEmptyParents(img);
        return;
      }
      const onErr = () => hideImgAndEmptyParents(img);
      const onLoad = () => {
        if (isPixelTracker(img)) hideImgAndEmptyParents(img);
      };
      img.addEventListener("error", onErr, { once: true });
      img.addEventListener("load", onLoad, { once: true });
    };

    root.querySelectorAll("img").forEach(process);

    // Cover late-inserted images (Outlook / Gmail sanitizers sometimes rewrite
    // src attributes after mount). We disconnect on unmount.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) {
            if (n.tagName === "IMG") process(n);
            n.querySelectorAll && n.querySelectorAll("img").forEach(process);
          }
        });
      }
    });
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [value, looksHtml]);

  if (!looksHtml) {
    return <p className={`whitespace-pre-wrap leading-relaxed ${className}`}>{value}</p>;
  }
  const clean = DOMPurify.sanitize(value, {
    ADD_ATTR: ["target", "rel", "style"],
    FORBID_TAGS: ["script", "iframe", "object", "embed"],
  });
  return (
    <div
      ref={ref}
      data-testid="rich-view"
      className={`prose prose-sm max-w-none prose-p:my-2 prose-headings:mt-3 prose-headings:mb-2 prose-ul:my-2 prose-ol:my-2 ${className}`}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
